import { SUGGESTED_PRICE_ENHANCEMENT_PHASE_DEFAULT } from '@enums/flags';
import { shouldShowSuggestedPricePerUnitColumn } from '@features/suggested-price-column';

import {
  DESCRIPTION_COLUMN,
  DISCOUNT_COLUMN,
  ID_COLUMN,
  ITEMS_PER_UNIT_COLUMN,
  LOGISTICS_CHARGE_COLUMN,
  NAME_COLUMN,
  NEGOTIATION_PRICE_PER_UNIT_COLUMN,
  NO_BID_COLUMN,
  PRICE_PER_UNIT_COLUMN,
  QUANTITY_OF_UNITS_COLUMN,
  QUANTITY_OF_ITEMS_COLUMN,
  REQUIRED_COLUMN,
  SUGGESTED_PRICE_PER_UNIT_COLUMN,
  UNIT_OF_MEASURE_COLUMN,
} from './constants';

/**
 * Defines the order of headers and items in the Buyer's export sheet
 * Do not change the order of these columns, as it is used to map the columns in the buyer's export file.
 * @type {string[]}
 */
const BUYER_ITEM_TYPES: string[] = [
  NAME_COLUMN,
  DESCRIPTION_COLUMN,
  UNIT_OF_MEASURE_COLUMN,
  ITEMS_PER_UNIT_COLUMN,
  NEGOTIATION_PRICE_PER_UNIT_COLUMN,
  PRICE_PER_UNIT_COLUMN,
  QUANTITY_OF_ITEMS_COLUMN,
  QUANTITY_OF_UNITS_COLUMN,
  REQUIRED_COLUMN
];

/**
 * Buyer export order when the Suggested Price enhancement phase flag is on. Swaps Baseline
 * (PRICE_PER_UNIT) ahead of Negotiation Starting Price to match the buyer HoT, and adds the
 * Suggested Price column after NSP. The Suggested Price slot is filtered out by
 * getColumnsToRemove when its drawer/config gates are not satisfied.
 */
const BUYER_ITEM_TYPES_PHASED: string[] = [
  NAME_COLUMN,
  DESCRIPTION_COLUMN,
  UNIT_OF_MEASURE_COLUMN,
  ITEMS_PER_UNIT_COLUMN,
  PRICE_PER_UNIT_COLUMN,
  NEGOTIATION_PRICE_PER_UNIT_COLUMN,
  SUGGESTED_PRICE_PER_UNIT_COLUMN,
  QUANTITY_OF_ITEMS_COLUMN,
  QUANTITY_OF_UNITS_COLUMN,
  REQUIRED_COLUMN
];

/**
 * Defines the order of headers and items in the Supplier's export sheet.
 * Do not change the order of these columns, as it is used to map the columns in supplier's export file.
 * @type {string[]}
 */
const SUPPLIER_ITEM_TYPES: string[] = [
  ID_COLUMN,
  NAME_COLUMN,
  DESCRIPTION_COLUMN,
  UNIT_OF_MEASURE_COLUMN,
  ITEMS_PER_UNIT_COLUMN,
  QUANTITY_OF_ITEMS_COLUMN,
  QUANTITY_OF_UNITS_COLUMN,
  PRICE_PER_UNIT_COLUMN,
  DISCOUNT_COLUMN,
  LOGISTICS_CHARGE_COLUMN,
  NO_BID_COLUMN
];

interface GetExportColumnNamesParams {
  canImportPostSubmission?: boolean;
  hasActiveSubmissions?: boolean;
  isInformation?: boolean;
  isPriceUpdate?: boolean;
  isSupplier: boolean;
  showDiscount: boolean;
  showLogisticsCharge: boolean;
  showPricePerUnit: boolean;
  showSuggestedPricePerUnitColumn?: boolean;
  suggestedPriceEnhancementPhase?: number;
  useSuggestedPricing?: boolean;
}

const getColumnsToRemove = ({
  isInformation = false,
  isPriceUpdate = false,
  showPricePerUnit,
  showLogisticsCharge,
  showDiscount,
  showSuggestedPricePerUnitColumn = false,
  suggestedPriceEnhancementPhase = SUGGESTED_PRICE_ENHANCEMENT_PHASE_DEFAULT,
  useSuggestedPricing = false,
}: Omit<GetExportColumnNamesParams, 'isSupplier'>): string[] => {
  const colsToRemove: string[] = [];

  colsToRemove.push(ITEMS_PER_UNIT_COLUMN, QUANTITY_OF_ITEMS_COLUMN);
  if (!showPricePerUnit) colsToRemove.push(PRICE_PER_UNIT_COLUMN);
  if (isPriceUpdate) colsToRemove.push(NEGOTIATION_PRICE_PER_UNIT_COLUMN);
  if (!showLogisticsCharge) colsToRemove.push(LOGISTICS_CHARGE_COLUMN);
  if (!showDiscount) colsToRemove.push(DISCOUNT_COLUMN);
  // Suggested Price is only present in BUYER_ITEM_TYPES_PHASED; filter it out unless all four gates
  // agree (see shouldShowSuggestedPricePerUnitColumn for the invariant).
  if (!shouldShowSuggestedPricePerUnitColumn({
    isInformation,
    isPriceUpdate,
    showSuggestedPricePerUnitColumn,
    suggestedPriceEnhancementPhase,
    useSuggestedPricing,
  })) colsToRemove.push(SUGGESTED_PRICE_PER_UNIT_COLUMN);

  return colsToRemove;
};

const removeColumns = (itemTypes: string[], colsToRemove: string[]): string[] => {
  return itemTypes.filter(col => !colsToRemove.includes(col));
};

export const getExportColumnNames = ({
  canImportPostSubmission = false,
  hasActiveSubmissions = false,
  isPriceUpdate = false,
  showPricePerUnit,
  showLogisticsCharge,
  showDiscount,
  showSuggestedPricePerUnitColumn = false,
  isSupplier,
  suggestedPriceEnhancementPhase = SUGGESTED_PRICE_ENHANCEMENT_PHASE_DEFAULT,
  useSuggestedPricing = false,
}: GetExportColumnNamesParams): string[] => {
  // Buyer path swaps to Baseline → NSP when the phase flag is on, matching the buyer HoT order.
  // Supplier export is unaffected — it has no Negotiation Starting Price column at all.
  const buyerItemTypes = suggestedPriceEnhancementPhase >= 1 ? BUYER_ITEM_TYPES_PHASED : BUYER_ITEM_TYPES;
  const itemTypes = isSupplier ? [...SUPPLIER_ITEM_TYPES] : [...buyerItemTypes];

  // Determine columns to remove based on flags
  const colsToRemove = getColumnsToRemove({
    isPriceUpdate,
    showDiscount,
    showLogisticsCharge,
    showPricePerUnit,
    showSuggestedPricePerUnitColumn,
    suggestedPriceEnhancementPhase,
    useSuggestedPricing,
  });

  // Remove the specified columns from item types
  const columns = removeColumns(itemTypes, colsToRemove);

  // Append Line Item ID as the last column when submissions exist (for post-submission re-import matching)
  if (hasActiveSubmissions && !isSupplier && canImportPostSubmission) {
    columns.push(ID_COLUMN);
  }

  return columns;
};
